import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router";
import { updateCollection, createScene } from "../../utils/api";
import { Scene } from "../../common/types";
import { FaEdit, FaSave, FaTimes, FaPlus, FaSync } from "react-icons/fa";
import "./Collection.css";
import { useScenes } from "../../context/ScenesContext";
import DurationSelector from "../../common/DurationSelector";

const Edit = FaEdit as unknown as React.FC;
const Save = FaSave as unknown as React.FC;
const Cancel = FaTimes as unknown as React.FC;
const Plus = FaPlus as unknown as React.FC;
const Times = FaTimes as unknown as React.FC;
const Sync = FaSync as unknown as React.FC;

export default function Collection() {
  const { collectionId } = useParams<{ collectionId: string }>();
  const navigate = useNavigate();
  const {
    scenes,
    refreshScenes,
    setCollectionId,
    collectionData,
    refreshCollectionData,
  } = useScenes();
  const [isEditing, setIsEditing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    client: ""
  });
  const [newScene, setNewScene] = useState({
    scene_id: "",
    scene_name: "",
    description: "",
    historical_year: "",
    longitude: "",
    latitude: ""
  });

  useEffect(() => {
    setCollectionId(collectionId);
    return () => setCollectionId(undefined);
  }, [collectionId, setCollectionId]);

  React.useEffect(() => {
    setEditForm({
      name: collectionData?.collection.name || "",
      description: collectionData?.collection.description || "",
      client: collectionData?.collection.client || ""
    });
  }, [collectionData]);

  const handleSave = async () => {
    if (!collectionId) return;
    try {
      await updateCollection(collectionId, editForm);
      setIsEditing(false);
      refreshCollectionData()
    } catch (error) {
      console.error("Error updating collection:", error);
    }
  };

  const handleCancel = () => {
    if (collectionData) {
      setEditForm({
        name: collectionData.collection.name,
        description: collectionData.collection.description,
        client: collectionData.collection.client
      });
    }
    setIsEditing(false);
  };

  const handleCreateScene = async () => {
    if (!collectionId || !newScene.scene_id.trim() || !newScene.scene_name.trim()) {
      alert("Scene ID and scene name are required.");
      return;
    }

    try {
      const sceneData: Partial<Scene> = {
        id: newScene.scene_id,
        description: newScene.description || undefined,
        historical_year: newScene.historical_year ? parseInt(newScene.historical_year) : undefined,
        longitude: newScene.longitude ? parseFloat(newScene.longitude) : undefined,
        latitude: newScene.latitude ? parseFloat(newScene.latitude) : undefined
      };

      const result = await createScene(collectionId, newScene.scene_name, sceneData);
      setShowCreateModal(false);
      setNewScene({
        scene_id: "",
        scene_name: "",
        description: "",
        historical_year: "",
        longitude: "",
        latitude: ""
      });
      refreshScenes();
      navigate(`/collection/${collectionId}/${result.id}`);
    } catch (error) {
      console.error("Error creating scene:", error);
      alert("Failed to create scene. Please try again.");
    }
  };

  if (!collectionData) {
    return (
      <div className="page-shell">
        <p>Loading collection...</p>
      </div>
    );
  }

  const { collection, topScenes, topCards } = collectionData;

  if (!collection || !scenes) {
    return <p>Loading...</p>;
  }

  return (
    <div className="page-shell collection-container">
      <nav className="breadcrumbs">
        <Link to="/">Home</Link>
        <span className="breadcrumb-sep">/</span>
        <span>{collection.name}</span>
      </nav>

      <div className="collection-header">
        <h1 className="page-title">{collection.name}</h1>
        {!isEditing && (
          <button className="btn-ark icon-btn" onClick={() => setIsEditing(true)}>
            <Edit /> Edit
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="edit-form">
          <div className="form-group">
            <label htmlFor="name">Collection Name</label>
            <input
              type="text"
              id="name"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label htmlFor="client">Client</label>
            <input
              type="text"
              id="client"
              value={editForm.client}
              onChange={(e) => setEditForm({ ...editForm, client: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              rows={4}
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
            />
          </div>
          <div className="form-actions">
            <button className="btn-ark" onClick={handleSave}>
              <Save /> Save
            </button>
            <button className="btn-secondary" onClick={handleCancel}>
              <Cancel /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="collection-info">
          <div className="info-row">
            <span className="info-label">Client:</span>
            <span className="info-value">{collection.client}</span>
          </div>
          {collection.description && (
            <div className="info-row">
              <span className="info-label">Description:</span>
              <span className="info-value">{collection.description}</span>
            </div>
          )}
          <div className="info-row">
            <span className="info-label">Created:</span>
            <span className="info-value">{new Date(collection.created).toLocaleDateString()}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Last Updated:</span>
            <span className="info-value">{new Date(collection.updated).toLocaleDateString()}</span>
          </div>
        </div>
      )}

      <section className="stats-section">
        <div className="stats-header">
          <h2 className="section-title">Top Viewed Scenes</h2>
          <div className="stats-controls">
            <DurationSelector />
            <button className="btn-secondary icon-btn" onClick={refreshCollectionData}>
              <Sync /> Refresh
            </button>
          </div>
        </div>
        {topScenes.length > 0 ? (
          <ul className="stats-list">
            {topScenes.map((scene) => (
              <li key={scene.scene_id} className="stats-item">
                <Link to={`/collection/${collectionId}/${scene.scene_id}`} className="stats-link">
                  <span className="stats-name">{scene.scene_name}</span>
                  <span className="stats-value">{scene.total_views} views</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="empty-state">No view data available yet.</p>
        )}
      </section>

      <section className="stats-section">
        <div className="stats-header">
          <h2 className="section-title">Top Viewed Cards</h2>
          <div className="stats-controls">
            <DurationSelector />
            <button className="btn-secondary icon-btn" onClick={refreshCollectionData}>
              <Sync /> Refresh
            </button>
          </div>
        </div>
        {topCards.length > 0 ? (
          <ul className="stats-list">
            {topCards.map((card) => (
              <li key={card.card_id} className="stats-item">
                <div className="stats-link">
                  <span className="stats-name">
                    {card.scene_name} - {card.card_id}
                  </span>
                  <span className="stats-value">{card.total_views} views</span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="empty-state">No view data available yet.</p>
        )}
      </section>

      <section className="scenes-section">
        <div className="section-header">
          <h2 className="section-title">All Scenes ({scenes!.length})</h2>
          <button 
            className="btn-ark icon-btn" 
            onClick={() => setShowCreateModal(true)}
          >
            <Plus /> Create Scene
          </button>
        </div>
        {scenes!.length > 0 ? (
          <ul className="home-scene-list">
            {scenes!.map((scene) => (
              <li className="home-scene-item" key={scene.id}>
                <Link className="home-scene-link" to={`/collection/${collectionId}/${scene.id}`}>
                  <span className="home-scene-name">{scene.scene_name}</span>
                  <span className="home-scene-count">{scene.cardsCount || 0} cards</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="empty-state">No scenes in this collection yet.</p>
        )}
      </section>

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Create New Scene</h2>
              <button 
                className="modal-close" 
                onClick={() => setShowCreateModal(false)}
              >
                <Times />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="scene_id">Scene ID*</label>
                <input
                  type="text"
                  id="scene_id"
                  value={newScene.scene_id}
                  onChange={(e) => setNewScene({ ...newScene, scene_id: e.target.value })}
                  placeholder="e.g. paris-2024"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="scene_name">Scene Name*</label>
                <input
                  type="text"
                  id="scene_name"
                  value={newScene.scene_name}
                  onChange={(e) => setNewScene({ ...newScene, scene_name: e.target.value })}
                  placeholder="Enter scene name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="scene_description">Description</label>
                <textarea
                  id="scene_description"
                  rows={4}
                  value={newScene.description}
                  onChange={(e) => setNewScene({ ...newScene, description: e.target.value })}
                  placeholder="Enter description (optional)"
                />
              </div>
              <div className="form-group">
                <label htmlFor="historical_year">Historical Year</label>
                <input
                  type="number"
                  id="historical_year"
                  value={newScene.historical_year}
                  onChange={(e) => setNewScene({ ...newScene, historical_year: e.target.value })}
                  placeholder="e.g., 1920"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="longitude">Longitude</label>
                  <input
                    type="number"
                    step="any"
                    id="longitude"
                    value={newScene.longitude}
                    onChange={(e) => setNewScene({ ...newScene, longitude: e.target.value })}
                    placeholder="e.g., -73.935242"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="latitude">Latitude</label>
                  <input
                    type="number"
                    step="any"
                    id="latitude"
                    value={newScene.latitude}
                    onChange={(e) => setNewScene({ ...newScene, latitude: e.target.value })}
                    placeholder="e.g., 40.730610"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-ark" onClick={handleCreateScene}>
                Create
              </button>
              <button 
                className="btn-secondary" 
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
